import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Folder, FolderPlus, Upload, Trash2, Download, ArrowLeft, Copy, ShieldCheck, UserPlus, Mail, X, Briefcase, Save, CalendarIcon, LayoutGrid, List, Link2, Activity, ExternalLink, Rocket, CheckCircle2, Circle } from "lucide-react";
import { BulkToolbar, useBulkSelection } from "@/components/admin/BulkToolbar";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import client, { API } from "@/api/client";

function fmtSize(bytes = 0) {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return iso;
  }
}

function memberRoomUrl(room_id) {
  return `${window.location.origin}/data-rooms/${room_id}`;
}

export function AdminDataRoomManager({ initialRoomId, onNavigateToCompany } = {}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Drill-through from the Overview → "Data rooms by stage": `?stage=x`
  // filters the list and auto-swaps into kanban view (which naturally
  // groups by stage) so the admin lands on the right column.
  const stageFilter = searchParams.get("stage") || "";
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(null); // 'upload' | 'delete-room' | 'add-access' | doc_id | email
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("viewer");
  const [viewMode, setViewMode] = useState(stageFilter ? "kanban" : "list"); // list | kanban
  const [activity, setActivity] = useState([]);
  const [activityBusy, setActivityBusy] = useState(false);
  const [program, setProgram] = useState(null);
  const fileRef = useRef(null);

  const loadRooms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/data-rooms");
      setRooms(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  const openRoom = async (roomId) => {
    try {
      const res = await client.get(`/admin/data-rooms/${roomId}`);
      setSelectedRoom(res.data.room);
      setDocs(res.data.documents || []);
      loadActivity(roomId);
      // If the room is linked to a company, fetch its program summary so
      // the Playbook strip inside the detail can render milestones inline.
      if (res.data.room?.company_id) {
        loadProgram(res.data.room.company_id);
      } else {
        setProgram(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const loadProgram = async (companyId) => {
    try {
      const res = await client.get(`/admin/companies/${companyId}/program`);
      setProgram(res.data);
    } catch (_) {
      setProgram(null);
    }
  };

  const toggleRoomMilestone = async (m) => {
    if (!selectedRoom?.company_id) return;
    const next = m.status === "done" ? "todo" : "done";
    try {
      await client.patch(`/admin/companies/${selectedRoom.company_id}/milestones/${m.entry_id}`, { status: next });
      loadProgram(selectedRoom.company_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  const loadActivity = async (roomId) => {
    setActivityBusy(true);
    try {
      const res = await client.get(`/admin/data-rooms/${roomId}/activity?limit=50`);
      setActivity(res.data?.items || []);
    } catch (e) {
      setActivity([]);
    } finally {
      setActivityBusy(false);
    }
  };

  // Deep link (from Companies page): open a specific room on mount.
  useEffect(() => {
    if (initialRoomId) openRoom(initialRoomId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRoomId]);

  const closeRoom = () => {
    setSelectedRoom(null);
    setDocs([]);
    setInviteEmail("");
    setActivity([]);
    setProgram(null);
  };

  const createRoom = async (e) => {
    e?.preventDefault?.();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await client.post("/admin/data-rooms", {
        company_name: name,
        description: newDesc.trim(),
      });
      setNewName("");
      setNewDesc("");
      toast.success(t("dataRoom.created"));
      await loadRooms();
      openRoom(res.data.room_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setCreating(false);
    }
  };

  const deleteRoom = async (roomId) => {
    if (!window.confirm(t("dataRoom.confirmDeleteRoom"))) return;
    setBusy("delete-room");
    try {
      await client.delete(`/admin/data-rooms/${roomId}`);
      toast.success(t("dataRoom.roomDeleted"));
      closeRoom();
      loadRooms();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedRoom) return;
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await client.post(`/admin/data-rooms/${selectedRoom.room_id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (fileRef.current) fileRef.current.value = "";
      toast.success(t("dataRoom.uploaded"));
      openRoom(selectedRoom.room_id);
      loadRooms();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const deleteDoc = async (doc_id) => {
    if (!selectedRoom) return;
    setBusy(doc_id);
    try {
      await client.delete(`/admin/data-rooms/${selectedRoom.room_id}/documents/${doc_id}`);
      toast.success(t("dataRoom.docDeleted"));
      openRoom(selectedRoom.room_id);
      loadRooms();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const addAccess = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !selectedRoom) return;
    setBusy("add-access");
    try {
      const res = await client.post(`/admin/data-rooms/${selectedRoom.room_id}/access`, { email, role: inviteRole });
      setSelectedRoom({ ...selectedRoom, allowed_emails: res.data.allowed_emails || [], members: res.data.members || [] });
      setInviteEmail("");
      toast.success(
        res.data.registered === false
          ? t("dataRoom.invitedUnregistered")
          : t("dataRoom.invited"),
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const removeAccess = async (email) => {
    if (!selectedRoom) return;
    setBusy(email);
    try {
      const res = await client.delete(
        `/admin/data-rooms/${selectedRoom.room_id}/access/${encodeURIComponent(email)}`,
      );
      setSelectedRoom({ ...selectedRoom, allowed_emails: res.data.allowed_emails || [] });
      toast.success(t("dataRoom.accessRevoked"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const copyRoomLink = () => {
    if (!selectedRoom) return;
    navigator.clipboard.writeText(memberRoomUrl(selectedRoom.room_id)).then(
      () => toast.success(t("dataRoom.linkCopied")),
      () => toast.error(t("common.error")),
    );
  };

  const downloadDoc = (doc_id, name) => {
    const a = document.createElement("a");
    a.href = `${API}/admin/data-rooms/${selectedRoom.room_id}/documents/${doc_id}/download`;
    a.download = name || "document";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const shareDoc = async (doc_id) => {
    if (!selectedRoom) return;
    setBusy(`share-${doc_id}`);
    try {
      const res = await client.post(`/admin/data-rooms/${selectedRoom.room_id}/documents/${doc_id}/signed-url`);
      const absolute = `${window.location.origin}${res.data.url}`;
      await navigator.clipboard.writeText(absolute).catch(() => {});
      toast.success(t("dataRoom.signedUrlIssued"));
      loadActivity(selectedRoom.room_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(null);
    }
  };

  const moveStage = async (room_id, stage) => {
    try {
      const res = await client.post(`/admin/data-rooms/${room_id}/stage`, { stage });
      setRooms((rs) => rs.map((r) => (r.room_id === room_id ? { ...r, ...res.data } : r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  // Bulk selection over the currently listed rooms.
  const bulk = useBulkSelection("data_rooms", (r) => r.room_id, () => {
    // Reload page 1 to pick up the changes without paging state gymnastics.
    (async () => {
      try {
        const res = await client.get(`/admin/data-rooms?page=1&per_page=50`);
        setRooms(res.data?.items || []);
      } catch (err) {
        console.warn("[AdminDataRoomManager] bulk reload failed", err);
      }
    })();
  });
  const bulkActions = [
    { key: "set_stage", label: t("dataRoom.bulkStage", { stage: t("adminOverview.roomStage.diligence") }), icon: <Briefcase className="h-3.5 w-3.5" />, value: "diligence" },
    { key: "set_stage", label: t("dataRoom.bulkStage", { stage: t("adminOverview.roomStage.ic_review") }), icon: <Briefcase className="h-3.5 w-3.5" />, value: "ic_review" },
    { key: "set_status", label: t("dataRoom.bulkCloseWon"), icon: <ShieldCheck className="h-3.5 w-3.5" />, value: "won" },
    { key: "set_status", label: t("dataRoom.bulkCloseLost"), icon: <ShieldCheck className="h-3.5 w-3.5" />, value: "lost" },
    {
      key: "delete",
      label: t("admin.delete"),
      icon: <Trash2 className="h-3.5 w-3.5" />,
      variant: "destructive",
      requiresConfirm: true,
      confirmTitle: t("dataRoom.bulkDeleteTitle"),
      confirmBody: t("dataRoom.bulkDeleteBody"),
    },
  ];

  const saveMeta = async (patch) => {
    if (!selectedRoom) return;
    try {
      const res = await client.patch(`/admin/data-rooms/${selectedRoom.room_id}`, patch);
      setSelectedRoom({ ...selectedRoom, ...res.data });
      loadRooms();
      toast.success(t("dataRoom.metaSaved"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    }
  };

  // ---------------------------------------------------------------- detail
  if (selectedRoom) {
    const memberUrl = memberRoomUrl(selectedRoom.room_id);
    const allowed = selectedRoom.allowed_emails || [];
    return (
      <div className="space-y-6" data-testid="admin-data-room-detail">
        <Card className="rounded-2xl border border-border p-6 space-y-5">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={closeRoom}
                className="h-9 w-9 rounded-lg"
                aria-label={t("dataRoom.back")}
                data-testid="admin-data-room-back"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0">
                <h2
                  className="font-display text-xl font-semibold truncate"
                  data-testid="admin-data-room-title"
                >
                  {selectedRoom.company_name}
                </h2>
                {selectedRoom.description && (
                  <p className="text-sm text-muted-foreground mt-1">{selectedRoom.description}</p>
                )}
                {selectedRoom.company_id && onNavigateToCompany && (
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    onClick={() => onNavigateToCompany(selectedRoom.company_id)}
                    data-testid="admin-data-room-open-company"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {t("dataRoom.openCompany")}
                  </button>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="rounded-xl text-destructive hover:text-destructive gap-1.5"
              onClick={() => deleteRoom(selectedRoom.room_id)}
              disabled={busy === "delete-room"}
              data-testid="admin-data-room-delete"
            >
              {busy === "delete-room" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              {t("dataRoom.deleteRoom")}
            </Button>
          </div>
        </Card>

        <DealMetaCard room={selectedRoom} onSave={saveMeta} />

        {/* Access allow-list */}
        <Card className="rounded-2xl border border-border p-6 space-y-4" data-testid="admin-data-room-access">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" />
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              {t("dataRoom.accessTitle")}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground">{t("dataRoom.accessHint")}</p>

          <form onSubmit={addAccess} className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs">{t("dataRoom.inviteEmailLabel")}</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="rounded-xl mt-1"
                placeholder="founder@company.com"
                required
                data-testid="admin-data-room-invite-input"
              />
            </div>
            <div className="w-[140px]">
              <Label className="text-xs">Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="admin-data-room-invite-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="owner">Owner</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit"
              className="btn-brand rounded-xl gap-2"
              disabled={busy === "add-access" || !inviteEmail.trim()}
              data-testid="admin-data-room-invite-submit"
            >
              {busy === "add-access" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
              {t("dataRoom.invite")}
            </Button>
          </form>

          {(selectedRoom.members?.length ? selectedRoom.members : allowed.map((e) => ({ email: e, role: "viewer" }))).length === 0 ? (
            <p className="text-sm text-muted-foreground pt-2" data-testid="admin-data-room-access-empty">
              {t("dataRoom.accessEmpty")}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2 pt-1">
              {(selectedRoom.members?.length ? selectedRoom.members : allowed.map((e) => ({ email: e, role: "viewer" }))).map((m) => (
                <span
                  key={m.email}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs"
                  data-testid={`admin-data-room-access-${m.email}`}
                >
                  <Mail className="h-3 w-3 text-muted-foreground" />
                  {m.email}
                  <span className="ml-1 text-[10px] uppercase tracking-wider text-primary" data-testid={`admin-data-room-role-${m.email}`}>
                    {m.role}
                  </span>
                  <button
                    type="button"
                    aria-label={t("dataRoom.revoke")}
                    onClick={() => removeAccess(m.email)}
                    className="ml-1 text-muted-foreground hover:text-destructive disabled:opacity-50"
                    disabled={busy === m.email}
                    data-testid={`admin-data-room-revoke-${m.email}`}
                  >
                    {busy === m.email ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                  </button>
                </span>
              ))}
            </div>
          )}

          <div className="rounded-xl border border-dashed border-border p-3 flex items-center gap-2 flex-wrap">
            <Input
              readOnly
              value={memberUrl}
              className="rounded-lg font-mono text-xs flex-1 min-w-[220px] bg-background"
              data-testid="admin-data-room-member-url"
            />
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg gap-2"
              onClick={copyRoomLink}
              data-testid="admin-data-room-copy-link"
            >
              <Copy className="h-3.5 w-3.5" /> {t("dataRoom.copyLink")}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">{t("dataRoom.linkPrivateNote")}</p>
        </Card>

        {/* Documents */}
        <Card className="rounded-2xl border border-border p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="font-display text-lg font-semibold">{t("dataRoom.documents")}</h3>
            <label>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={uploadDoc}
                data-testid="admin-data-room-upload-input"
              />
              <Button asChild className="btn-brand rounded-xl gap-2" type="button" disabled={busy === "upload"}>
                <span>
                  {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {busy === "upload" ? t("dataRoom.uploading") : t("dataRoom.upload")}
                </span>
              </Button>
            </label>
          </div>
          <p className="text-xs text-muted-foreground">{t("dataRoom.uploadHint")}</p>

          {docs.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center" data-testid="admin-data-room-empty">
              {t("dataRoom.noDocs")}
            </p>
          ) : (
            <div className="divide-y divide-border">
              {docs.map((d) => (
                <div key={d.doc_id} className="flex items-center gap-3 py-3" data-testid={`admin-data-room-doc-${d.doc_id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{d.original_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtSize(d.size_bytes)} · {fmtDate(d.uploaded_at)}
                    </p>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => downloadDoc(d.doc_id, d.original_name)}
                    aria-label={t("dataRoom.download")}
                    data-testid={`admin-data-room-download-${d.doc_id}`}
                  >
                    <Download className="h-4 w-4 text-primary" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => shareDoc(d.doc_id)}
                    aria-label={t("dataRoom.signedUrl")}
                    disabled={busy === `share-${d.doc_id}`}
                    data-testid={`admin-data-room-share-${d.doc_id}`}
                    title={t("dataRoom.signedUrlHint")}
                  >
                    {busy === `share-${d.doc_id}` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Link2 className="h-4 w-4 text-primary" />
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => deleteDoc(d.doc_id)}
                    disabled={busy === d.doc_id}
                    aria-label={t("dataRoom.deleteDoc")}
                    data-testid={`admin-data-room-doc-delete-${d.doc_id}`}
                  >
                    {busy === d.doc_id ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-destructive" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Playbook strip — only appears when the room is linked to a
            company that has been enrolled in a Program. The whole card
            is admin-only view; founder-side counterpart lives on the
            Dashboard. */}
        {program?.program && program?.milestones?.length > 0 && (
          <Card className="rounded-2xl border border-border p-6 space-y-3" data-testid="admin-data-room-playbook">
            <div className="flex items-center gap-2 flex-wrap">
              <Rocket className="h-4 w-4 text-primary" />
              <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                Playbook · {program.program.name}
              </h3>
              <span className="ml-auto font-mono text-xs text-muted-foreground" data-testid="admin-data-room-playbook-progress">
                {program.progress.done}/{program.progress.total} · {program.progress.percent}%
              </span>
            </div>
            <Progress value={program.progress.percent} className="h-1.5" />
            <ol className="divide-y divide-border">
              {program.milestones.slice(0, 6).map((m) => (
                <li key={m.entry_id} className="flex items-start gap-3 py-2" data-testid={`admin-data-room-playbook-ms-${m.entry_id}`}>
                  <button
                    type="button"
                    onClick={() => toggleRoomMilestone(m)}
                    className={m.status === "done" ? "text-primary" : "text-muted-foreground hover:text-primary"}
                    aria-label={m.status === "done" ? "Reopen milestone" : "Mark done"}
                    data-testid={`admin-data-room-playbook-toggle-${m.entry_id}`}
                  >
                    {m.status === "done" ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm ${m.status === "done" ? "line-through text-muted-foreground" : ""}`}>{m.title}</p>
                    {m.stage && <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{stageLabel(t, m.stage)}</p>}
                  </div>
                </li>
              ))}
            </ol>
            {program.milestones.length > 6 && (
              <p className="text-[11px] text-muted-foreground">
                +{program.milestones.length - 6} more milestones — full list in the Company panel.
              </p>
            )}
          </Card>
        )}

        {/* Activity log */}
        <Card className="rounded-2xl border border-border p-6 space-y-3" data-testid="admin-data-room-activity">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" />
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              {t("dataRoom.activityTitle")}
            </h3>
            {activityBusy && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground ml-auto" />}
          </div>
          {activity.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="admin-data-room-activity-empty">
              {t("dataRoom.activityEmpty")}
            </p>
          ) : (
            <ol className="divide-y divide-border text-sm">
              {activity.map((a) => (
                <li key={a.activity_id} className="flex items-start gap-3 py-2" data-testid={`admin-data-room-activity-${a.activity_id}`}>
                  <span className="inline-flex items-center rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground font-mono">
                    {a.event}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate">
                      <span className="text-muted-foreground">by </span>
                      <span className="font-medium">{a.actor_email || a.actor_user_id || "system"}</span>
                    </p>
                    {a.meta && Object.keys(a.meta).length > 0 && (
                      <p className="text-xs text-muted-foreground font-mono truncate">
                        {Object.entries(a.meta).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ")}
                      </p>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(a.created_at)}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    );
  }

  // ----- rooms list -----
  return (
    <div className="space-y-6" data-testid="admin-panel-data-room">
      <Card className="rounded-2xl border border-border p-6 space-y-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Folder className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-xl font-semibold">{t("dataRoom.title")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("dataRoom.description")}</p>
          </div>
        </div>
        <form onSubmit={createRoom} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-xs">{t("dataRoom.companyName")}</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="rounded-xl mt-1"
              placeholder="Acme KSA"
              required
              data-testid="admin-data-room-new-name"
            />
          </div>
          <div>
            <Label className="text-xs">{t("dataRoom.descriptionLabel")}</Label>
            <Input
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              className="rounded-xl mt-1"
              placeholder="Series A diligence"
              data-testid="admin-data-room-new-desc"
            />
          </div>
          <Button
            className="btn-brand rounded-xl gap-2"
            type="submit"
            disabled={creating || !newName.trim()}
            data-testid="admin-data-room-create"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderPlus className="h-4 w-4" />}
            {t("dataRoom.createRoom")}
          </Button>
        </form>
      </Card>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : rooms.length === 0 ? (
        <Card className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="admin-data-room-empty-list">
          <Folder className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t("dataRoom.emptyList")}</p>
        </Card>
      ) : (
        <>
          <div className="flex items-center justify-end gap-1 rounded-xl border border-border p-1 w-fit ml-auto" data-testid="admin-data-room-view-toggle">
            <Button
              type="button"
              variant={viewMode === "list" ? "default" : "ghost"}
              size="sm"
              className={`rounded-lg gap-1.5 ${viewMode === "list" ? "btn-brand" : ""}`}
              onClick={() => setViewMode("list")}
              data-testid="admin-data-room-view-list"
            >
              <List className="h-3.5 w-3.5" /> {t("dataRoom.viewList")}
            </Button>
            <Button
              type="button"
              variant={viewMode === "kanban" ? "default" : "ghost"}
              size="sm"
              className={`rounded-lg gap-1.5 ${viewMode === "kanban" ? "btn-brand" : ""}`}
              onClick={() => setViewMode("kanban")}
              data-testid="admin-data-room-view-kanban"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> {t("dataRoom.viewKanban")}
            </Button>
          </div>

          {stageFilter && (
            <div className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2" data-testid="admin-data-room-stage-filter-banner">
              <span className="text-xs text-muted-foreground">{t("dataRoom.filteredByStage")}</span>
              <span className="text-xs font-medium">{stageLabel(t, stageFilter)}</span>
              <button
                type="button"
                className="ml-auto text-[11px] text-primary hover:underline"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete("stage");
                  setSearchParams(next, { replace: true });
                }}
                data-testid="admin-data-room-stage-filter-clear"
              >
                Clear filter
              </button>
            </div>
          )}

          {(() => {
            const _filteredRooms = stageFilter ? rooms.filter((r) => (r.stage || "") === stageFilter) : rooms;
            return viewMode === "list" ? (
            <>
              <BulkToolbar {...bulk} actions={bulkActions} testId="admin-data-room-bulk" onClear={bulk.clear} />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {_filteredRooms.map((r) => {
                  const checked = bulk.isSelected(r.room_id);
                  return (
                    <Card
                      key={r.room_id}
                      className={`rounded-2xl border p-5 hover:border-primary/40 hover:shadow-md transition-all ${checked ? "border-primary/70 bg-primary/5" : "border-border"}`}
                      data-testid={`admin-data-room-card-${r.room_id}`}
                    >
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => { e.stopPropagation(); bulk.toggle(r.room_id); }}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1"
                          aria-label={`Select ${r.company_name}`}
                          data-testid={`admin-data-room-select-${r.room_id}`}
                        />
                        <button
                          type="button"
                          onClick={() => openRoom(r.room_id)}
                          className="flex items-start gap-3 min-w-0 text-left flex-1"
                        >
                          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0">
                            <Folder className="h-5 w-5" />
                          </span>
                          <div className="min-w-0 flex-1">
                            <p className="font-display font-semibold truncate">{r.company_name}</p>
                            {r.description && (
                              <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{r.description}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                              <span>{r.document_count} {t("dataRoom.docsShort")}</span>
                              <span>·</span>
                              <span>{(r.allowed_emails?.length || 0)} {t("dataRoom.membersShort")}</span>
                              <span>·</span>
                              <span>{r.stage ? stageLabel(t, r.stage) : t("dataRoom.unstaged")}</span>
                              <span>·</span>
                              <span>{fmtDate(r.updated_at)}</span>
                            </div>
                          </div>
                        </button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </>
          ) : (
            <KanbanBoard rooms={_filteredRooms} openRoom={openRoom} moveStage={moveStage} t={t} />
          );
          })()}
        </>
      )}
    </div>
  );
}

export default AdminDataRoomManager;

// ---------------------------------------------------------------------------
// KanbanBoard — pipeline view. HTML5 drag-and-drop is enough here: we
// only need to move one card between six columns, no reordering inside
// a column, no touch/mobile-first requirements (kanban is desktop-only
// by design; the list view stays the mobile default).
// ---------------------------------------------------------------------------
/** Pipeline stage labels are shared with the admin overview i18n namespace. */
const stageLabel = (t, key) =>
  key
    ? t(`adminOverview.roomStage.${key}`, { defaultValue: String(key).replace(/_/g, " ") })
    : t("dataRoom.unstaged");

const KANBAN_STAGE_KEYS = ["", "sourced", "diligence", "ic_review", "term_sheet", "closing", "closed"];

function KanbanBoard({ rooms, openRoom, moveStage, t }) {
  const [dragOver, setDragOver] = useState(null);
  const buckets = KANBAN_STAGE_KEYS.map((key) => ({
    key,
    label: stageLabel(t, key),
    items: rooms.filter((r) => (r.stage || "") === key),
  }));

  const onDrop = (e, stage) => {
    e.preventDefault();
    const roomId = e.dataTransfer.getData("text/plain");
    setDragOver(null);
    if (!roomId) return;
    const current = rooms.find((r) => r.room_id === roomId);
    if (current && (current.stage || "") === stage) return;
    moveStage(roomId, stage);
  };

  return (
    <div className="grid grid-flow-col auto-cols-[minmax(240px,1fr)] gap-3 overflow-x-auto pb-2" data-testid="admin-data-room-kanban">
      {buckets.map((b) => (
        <div
          key={b.key || "_none"}
          className={`rounded-2xl border border-border bg-muted/20 p-3 min-h-[240px] transition-colors ${dragOver === b.key ? "border-primary bg-primary/5" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(b.key); }}
          onDragLeave={() => setDragOver((c) => (c === b.key ? null : c))}
          onDrop={(e) => onDrop(e, b.key)}
          data-testid={`admin-data-room-kanban-col-${b.key || "unstaged"}`}
        >
          <div className="flex items-center justify-between mb-2">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{b.label}</p>
            <span className="text-[11px] text-muted-foreground font-mono">{b.items.length}</span>
          </div>
          <div className="space-y-2">
            {b.items.length === 0 && (
              <div className="rounded-lg border border-dashed border-border p-3 text-center text-[11px] text-muted-foreground">
                {t("dataRoom.kanbanDrop")}
              </div>
            )}
            {b.items.map((r) => (
              <div
                key={r.room_id}
                draggable
                onDragStart={(e) => { e.dataTransfer.setData("text/plain", r.room_id); }}
                onClick={() => openRoom(r.room_id)}
                className="rounded-xl border border-border bg-card p-3 cursor-grab active:cursor-grabbing hover:border-primary/40 hover:shadow-sm transition-all"
                data-testid={`admin-data-room-kanban-card-${r.room_id}`}
              >
                <p className="font-medium truncate text-sm">{r.company_name}</p>
                {r.description && (
                  <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5">{r.description}</p>
                )}
                <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground">
                  <span>{r.document_count} {t("dataRoom.docsShort")}</span>
                  <span>·</span>
                  <span>{t(`dealMeta.priority_${r.priority || "medium"}`, { defaultValue: r.priority || "medium" })}</span>
                  {r.deal_size && (<><span>·</span><span>{r.deal_size}</span></>)}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DealMetaCard — classify / prioritize / assign the room. All fields are
// optional. Saves are per-field via PATCH so a partial edit never wipes a
// neighbouring value.
// ---------------------------------------------------------------------------
const STAGE_VALUES = ["", "sourced", "diligence", "ic_review", "term_sheet", "closing", "closed", "on_hold"];
const STATUS_VALUES = ["open", "won", "lost", "on_hold"];
const PRIORITY_VALUES = ["low", "medium", "high"];
const STATUS_BADGE = {
  open: "bg-primary/10 text-primary border-primary/30",
  won: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30 dark:text-emerald-400",
  lost: "bg-destructive/15 text-destructive border-destructive/30",
  on_hold: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
};

function DealMetaCard({ room, onSave }) {
  const { t } = useTranslation();
  const [local, setLocal] = useState({
    industry: room.industry || "",
    stage: room.stage || "",
    status: room.status || "open",
    priority: room.priority || "medium",
    deal_size: room.deal_size || "",
    expected_close: room.expected_close || "",
    assignee_email: room.assignee_email || "",
    tags: room.tags || [],
    notes: room.notes || "",
  });
  const [tagDraft, setTagDraft] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLocal({
      industry: room.industry || "",
      stage: room.stage || "",
      status: room.status || "open",
      priority: room.priority || "medium",
      deal_size: room.deal_size || "",
      expected_close: room.expected_close || "",
      assignee_email: room.assignee_email || "",
      tags: room.tags || [],
      notes: room.notes || "",
    });
    // Intentional: reset local edit state only when the user switches rooms.
    // Live-updating on every room prop change would clobber unsaved edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room.room_id]);

  const set = (k, v) => setLocal((s) => ({ ...s, [k]: v }));

  const addTag = (e) => {
    e?.preventDefault?.();
    const t = tagDraft.trim();
    if (!t) return;
    if (local.tags.includes(t)) {
      setTagDraft("");
      return;
    }
    set("tags", [...local.tags, t]);
    setTagDraft("");
  };

  const removeTag = (t) => set("tags", local.tags.filter((x) => x !== t));

  const save = async () => {
    setSaving(true);
    try {
      await onSave(local);
    } finally {
      setSaving(false);
    }
  };

  const statusClass = STATUS_BADGE[local.status] || STATUS_BADGE.open;

  return (
    <Card className="rounded-2xl border border-border p-6 space-y-4" data-testid="admin-data-room-meta">
      <div className="flex items-center gap-2">
        <Briefcase className="h-4 w-4 text-primary" />
        <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
          {t("dealMeta.title")}
        </h3>
        <span
          className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] uppercase tracking-widest ${statusClass}`}
          data-testid="admin-data-room-status-badge"
        >
          {t(`dealMeta.status_${local.status}`, { defaultValue: local.status.replace("_", " ") })}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs">{t("dealMeta.industry")}</Label>
          <Input value={local.industry} onChange={(e) => set("industry", e.target.value)} className="rounded-xl mt-1" placeholder={t("dealMeta.industryPlaceholder")} data-testid="admin-data-room-industry" />
        </div>
        <div>
          <Label className="text-xs">{t("dealMeta.stage")}</Label>
          <Select value={local.stage || "_none"} onValueChange={(v) => set("stage", v === "_none" ? "" : v)}>
            <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="admin-data-room-stage">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STAGE_VALUES.map((v) => (
                <SelectItem key={v || "_none"} value={v || "_none"}>{v ? stageLabel(t, v) : "—"}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("dealMeta.statusLabel")}</Label>
          <Select value={local.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="admin-data-room-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_VALUES.map((v) => (
                <SelectItem key={v} value={v}>{t(`dealMeta.status_${v}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("dealMeta.priority")}</Label>
          <Select value={local.priority} onValueChange={(v) => set("priority", v)}>
            <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="admin-data-room-priority">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIORITY_VALUES.map((v) => (
                <SelectItem key={v} value={v}>{t(`dealMeta.priority_${v}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">{t("dealMeta.dealSize")}</Label>
          <Input value={local.deal_size} onChange={(e) => set("deal_size", e.target.value)} className="rounded-xl mt-1" placeholder="$5M Series A" data-testid="admin-data-room-deal-size" />
        </div>
        <div>
          <Label className="text-xs">{t("dealMeta.expectedClose")}</Label>
          <div className="mt-1 flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  type="button"
                  className="rounded-xl flex-1 justify-start font-normal h-10"
                  data-testid="admin-data-room-expected-close"
                >
                  <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                  {local.expected_close ? format(parseISO(local.expected_close), "MMM d, yyyy") : (
                    <span className="text-muted-foreground">{t("dealMeta.pickDate")}</span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 w-auto" align="start">
                <Calendar
                  mode="single"
                  selected={local.expected_close ? parseISO(local.expected_close) : undefined}
                  onSelect={(d) => set("expected_close", d ? format(d, "yyyy-MM-dd") : "")}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {local.expected_close && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-10 w-10 rounded-xl text-muted-foreground hover:text-destructive"
                onClick={() => set("expected_close", "")}
                aria-label={t("common.clear")}
                data-testid="admin-data-room-expected-close-clear"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="sm:col-span-2 lg:col-span-3">
          <Label className="text-xs">{t("dealMeta.assignee")}</Label>
          <Input type="email" value={local.assignee_email} onChange={(e) => set("assignee_email", e.target.value.toLowerCase())} className="rounded-xl mt-1" placeholder="ops@example.com" data-testid="admin-data-room-assignee" />
        </div>
      </div>

      <div>
        <Label className="text-xs">{t("dealMeta.tags")}</Label>
        <form onSubmit={addTag} className="flex items-center gap-2 mt-1">
          <Input value={tagDraft} onChange={(e) => setTagDraft(e.target.value)} className="rounded-xl flex-1" placeholder={t("dealMeta.tagPlaceholder")} data-testid="admin-data-room-tag-input" />
          <Button type="submit" variant="outline" className="rounded-xl" data-testid="admin-data-room-tag-add">{t("dealMeta.add")}</Button>
        </form>
        {local.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {local.tags.map((t) => (
              <span key={t} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs" data-testid={`admin-data-room-tag-${t}`}>
                {t}
                <button type="button" aria-label={`remove ${t}`} onClick={() => removeTag(t)} className="text-muted-foreground hover:text-destructive">
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <Label className="text-xs">{t("dealMeta.notes")}</Label>
        <textarea value={local.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1 w-full rounded-xl border border-input bg-transparent p-3 text-sm" placeholder={t("dealMeta.notesPlaceholder")} data-testid="admin-data-room-notes" />
      </div>

      <div className="flex justify-end">
        <Button onClick={save} className="btn-brand rounded-xl gap-2" disabled={saving} data-testid="admin-data-room-meta-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {t("dealMeta.save")}
        </Button>
      </div>
    </Card>
  );
}
